/** All in-app routes — mirrors Flutter `AppRoutes` + staff nav catalog. */
export const AppPaths = {
  login: '/login',
  acceptInvite: '/accept-invite',
  home: '/home',

  /** Boss (plain Administrator) overview. Dashboard and payrolls are parked. */
  bossDashboard: '/overview',
  bossPayrolls: '/overview/payrolls',
  bossAttendance: '/overview/attendance',
  inHouseAttendance: '/overview/in-house-attendance',
  inHouseStaff: '/workforce?workforceType=InHouse',

  // Recruitment
  recruitmentJobs: '/recruitment/jobs',
  recruitmentJobDetail: (jobId: string) =>
    `/recruitment/jobs/${encodeURIComponent(jobId)}`,
  recruitmentApplicants: '/recruitment/applicants',
  recruitmentApplicantDetail: (candidateId: string) =>
    `/recruitment/applicants/${encodeURIComponent(candidateId)}`,
  recruitmentTalentPool: '/recruitment/talent-pool',
  recruitmentTalentPoolDetail: (candidateId: string) =>
    `/recruitment/talent-pool/${encodeURIComponent(candidateId)}`,
  recruitmentJobWizard: '/recruitment/job-wizard',

  // HR onboarding
  hrOnboardingHub: '/hr/onboarding',
  /** Onboarding desk queue for visa renewals asked from an employee record. */
  hrOnboardingRenewals: '/hr/onboarding/renewals',
  hrOnboardingStart: '/hr/onboarding/start',
  hrWalkInIntake: '/hr/onboarding/walk-in',
  /** HR desk: open cases with HR vs with PRO — follow-up only. */
  hrOnboardingFollowUp: '/hr/onboarding/follow-up',
  hrOnboardingCase: (caseId: string) =>
    `/hr/onboarding/cases/${encodeURIComponent(caseId)}`,
  hrJoiningFormCase: (caseId: string) =>
    `/hr/onboarding/joining-form/${encodeURIComponent(caseId)}`,
  hrProHandoverCase: (caseId: string) =>
    `/hr/onboarding/pro-handover/${encodeURIComponent(caseId)}`,
  hrManualIntake: (kind: string) =>
    `/hr/onboarding/new-hire/${encodeURIComponent(kind)}`,

  hrStaffPersonalDetails: '/hr/staff-personal-details',
  // Sibling of the list rather than nested, so the list's own prefix highlighting stays exact.
  hrStaffPersonalDetailsImport: '/hr/staff-personal-details-import',
  hrStaffPersonalDetail: (id: string) =>
    `/hr/staff-personal-details/${encodeURIComponent(id)}`,

  hrInterviewApplications: '/hr/interview-applications',
  hrInterviewApplication: (id: string) =>
    `/hr/interview-applications/${encodeURIComponent(id)}`,

  // Workforce
  workforceDirectory: '/workforce',
  workforceCompleteness: '/workforce/completeness',
  workforceStaffing: '/workforce/staffing',
  workforceAddEmployee: '/workforce/employees/add',
  workforceEmployee: (employeeId: string) =>
    `/workforce/employees/${encodeURIComponent(employeeId)}`,
  workforcePurgedEmployees: '/admin/purged-employees',

  // Payroll
  payroll: '/payroll',
  payrollHowItWorks: '/payroll/how-it-works',
  payrollAdminConsole: '/payroll/admin',
  payrollAdminDashboard: '/payroll/admin/dashboard',
  payrollAdminDashboardScoped: (opts: {
    clientId?: string | null
    anchorMonth?: string | null
    periodId?: string | null
  }) => {
    const params = new URLSearchParams()
    if (opts.clientId) {
      params.set('clientId', opts.clientId)
      if (opts.periodId) params.set('periodId', opts.periodId)
    } else if (opts.anchorMonth) {
      params.set('anchorMonth', opts.anchorMonth)
    }
    const q = params.toString()
    return q ? `/payroll/admin/dashboard?${q}` : '/payroll/admin/dashboard'
  },
  payrollAdminFormulas: '/payroll/admin/formulas',
  payrollIdentities: '/payroll/admin/identities',
  payrollIdentityDetail: (employeeId: string) =>
    `/payroll/admin/identities/${encodeURIComponent(employeeId)}`,
  payrollAdminAssignments: '/payroll/admin/assignments', // redirects to workforceDeployment
  workforceDeployment: '/payroll/admin/deployment',
  workforceDeploymentDetail: '/payroll/admin/deployment/detail',
  payrollAdminStaff: '/payroll/admin/staff',
  payrollAdminPublicHolidays: '/payroll/admin/public-holidays',
  payrollAdminStaffCostRates: '/payroll/admin/staff-cost-rates',
  payrollAdminPayModels: '/payroll/admin/pay-models',
  payrollAdminSettlements: '/payroll/admin/settlements',
  payrollAdminStaffCard: (employeeId: string) =>
    `/payroll/admin/staff/${encodeURIComponent(employeeId)}`,
  payrollAdminRuns: '/payroll/admin/runs',
  payrollAdminRun: (runId: string) =>
    `/payroll/admin/runs/${encodeURIComponent(runId)}`,
  payrollAdminPeriod: (periodId: string) =>
    `/payroll/admin/periods/${encodeURIComponent(periodId)}`,
  payrollAdminPayMonth: (anchorMonth: string) =>
    `/payroll/admin/pay-months/${encodeURIComponent(anchorMonth)}`,
  payrollAccounts: '/payroll/accounts',
  payrollAccountsPeople: '/payroll/accounts/people',
  payrollAccountsClients: '/payroll/accounts/clients',
  payrollRuns: '/payroll/runs',
  payrollRun: (runId: string) => `/payroll/runs/${encodeURIComponent(runId)}`,
  payrollSupervisorRosters: '/payroll/supervisor-rosters',
  payrollDailyAttendance: '/payroll/daily-attendance',
  payrollDailyAttendanceFor: (opts: { clientId?: string | null; month?: string | null }) => {
    const params = new URLSearchParams()
    if (opts.clientId) params.set('clientId', opts.clientId)
    if (opts.month) params.set('month', opts.month)
    const q = params.toString()
    return q ? `/payroll/daily-attendance?${q}` : '/payroll/daily-attendance'
  },
  payrollEmployeeAttendanceDetail: (employeeId: string) =>
    `/payroll/daily-attendance/employee/${encodeURIComponent(employeeId)}`,
  payrollTeamAttendance: '/payroll/team-attendance',
  /** Supervisor desk: people who have signed in to the mobile app at least once. */
  supervisorMobileApp: '/payroll/mobile-app',
  payrollAttendanceIssues: '/payroll/attendance-issues',
  payrollAttendanceNotifications: '/payroll/attendance-notifications',
  /** Team attendance list with optional view/date query (supervisor desk). */
  payrollTeamAttendanceFor: (opts?: {
    view?: 'day' | 'punches' | 'period' | 'overtime' | 'dutyHours' | null
    date?: string | null
    clientId?: string | null
    site?: string | null
    q?: string | null
    punchFilter?: string | null
  }) => {
    const params = new URLSearchParams()
    if (opts?.view) params.set('view', opts.view)
    if (opts?.date) params.set('date', opts.date)
    if (opts?.clientId) params.set('clientId', opts.clientId)
    if (opts?.site) params.set('site', opts.site)
    if (opts?.q) params.set('q', opts.q)
    if (opts?.punchFilter && opts.punchFilter !== 'all') params.set('punchFilter', opts.punchFilter)
    const q = params.toString()
    return q ? `/payroll/team-attendance?${q}` : '/payroll/team-attendance'
  },
  /** Punch day detail under team attendance: /payroll/team-attendance/employee/:id/punch/:date */
  payrollTeamPunchDay: (
    employeeId: string,
    date: string,
    opts?: {
      from?: 'day' | 'punches' | 'issues' | null
      /** Step through the day roster the supervisor was viewing. */
      roster?: boolean | null
      clientId?: string | null
      site?: string | null
      q?: string | null
      punchFilter?: string | null
    },
  ) => {
    const base = `/payroll/team-attendance/employee/${encodeURIComponent(employeeId)}/punch/${encodeURIComponent(date)}`
    const params = new URLSearchParams()
    if (opts?.from === 'day' || opts?.from === 'punches' || opts?.from === 'issues') {
      params.set('from', opts.from)
    }
    if (opts?.roster) params.set('roster', '1')
    if (opts?.clientId) params.set('clientId', opts.clientId)
    if (opts?.site) params.set('site', opts.site)
    if (opts?.q) params.set('q', opts.q)
    if (opts?.punchFilter && opts.punchFilter !== 'all') params.set('punchFilter', opts.punchFilter)
    const query = params.toString()
    return query ? `${base}?${query}` : base
  },
  supervisorMaterialRequest: '/material-request',
  supervisorGatePass: '/gate-pass',
  hrGatePasses: '/hr/gate-passes',
  hrGatePassRegister: '/hr/gate-passes/register',
  hrGatePass: (requestId: string) => `/hr/gate-passes/${encodeURIComponent(requestId)}`,
  adminGatePasses: '/admin/gate-passes',
  adminGatePass: (requestId: string) =>
    `/admin/gate-passes/${encodeURIComponent(requestId)}`,
  payrollTeamAvailability: '/payroll/team-availability',
  payrollEmployeeWorkspace: '/payroll/employee-workspace',
  payrollSelfAttendance: '/payroll/self-attendance',
  supervisorBulkHiring: '/supervisor/bulk-hiring',
  supervisorBulkHiringPerson: (caseId: string) =>
    `/supervisor/bulk-hiring/${encodeURIComponent(caseId)}`,
  hiringRequisitions: '/supervisor/hiring-requisitions',
  hiringRequisitionNew: '/supervisor/hiring-requisitions/new',
  hiringRequisition: (id: string) => `/supervisor/hiring-requisitions/${encodeURIComponent(id)}`,
  hiringRequisitionDecide: (id: string, seat: number) =>
    `/hiring-requisitions/${encodeURIComponent(id)}/decide?seat=${seat}`,
  recruitmentHiringRequisitions: '/recruitment/hiring-requisitions',
  supervisorStaffDocs: '/supervisor/staff-documents',
  supervisorStaffDocsEmployee: (employeeId: string) =>
    `/supervisor/staff-documents/${encodeURIComponent(employeeId)}`,

  // Admin
  adminRecruitmentSearch: '/admin/recruitment-search',
  adminClients: '/admin/clients',
  adminContracts: '/admin/contracts',
  accountsContracts: '/accounts/contracts',
  adminClientCard: (clientId: string) =>
    `/admin/clients/${encodeURIComponent(clientId)}`,
  adminClientEconomics: (clientId: string, periodId?: string) => {
    const base = `/admin/clients/${encodeURIComponent(clientId)}/economics`
    return periodId ? `${base}?periodId=${encodeURIComponent(periodId)}` : base
  },
  adminAnalytics: '/admin/analytics',
  adminStaffOperations: '/admin/staff-operations',
  adminNotificationRules: '/admin/notification-rules',
  /** Boss attendance alert schedules (Alerts module). */
  adminAttendanceAlerts: '/admin/attendance-alerts',
  adminAttendanceAlertRuns: '/admin/attendance-alerts-runs',
  adminContactInquiries: '/admin/contact-inquiries',
  adminContactInquiry: (id: string) =>
    `/admin/contact-inquiries/${encodeURIComponent(id)}`,
  adminAccessControl: '/admin/access-control',
  adminAccessControlFeatures: '/admin/access-control/features',
  adminAccessControlRoles: '/admin/access-control/roles',
  adminTeam: '/admin/team',
  adminTeamRoles: '/admin/team/roles',
  adminTeamMember: (userId: string) => `/admin/team/${encodeURIComponent(userId)}`,

  // Finance ledger + client invoicing
  financeOverview: '/finance',
  financeMovements: '/finance/movements',
  invoicing: '/invoicing',

  // Leave management (admin)
  adminLeaves: '/admin/leaves',
  adminLeavePolicies: '/admin/leaves/policies',
  adminEmployeeLeaves: (employeeId: string) =>
    `/admin/leaves/employees/${encodeURIComponent(employeeId)}`,
  adminDesktopActivity: '/admin/desktop-activity',
  myDesktopActivity: '/account/desktop-activity',

  /** Redirects to tenant-scoped stocks dashboard (`/t/:tenantId/stocks`). */
  adminStocks: '/admin/stocks',
  /** Redirects to tenant-scoped stock approvals (`/t/:tenantId/stocks/approvals`). */
  adminStockApprovals: '/admin/stock-approvals',

  // Sales desk
  salesDashboard: '/sales',
  salesProspects: '/sales/prospects',
  salesProspectDetail: (prospectId: string) =>
    `/sales/prospects/${encodeURIComponent(prospectId)}`,
  salesAccounts: '/sales/accounts',
  salesCases: '/sales/cases',
  salesCaseDetail: (caseId: string) =>
    `/sales/cases/${encodeURIComponent(caseId)}`,

  // Client portal (staff view)
  clientApplicants: '/client/applicants',
  clientApplicantDetail: (applicationId: string) =>
    `/client/applicants/${encodeURIComponent(applicationId)}`,

  // PRO
  proOnboardingHub: '/pro/onboarding',
  proOnboardingCase: (caseId: string) =>
    `/pro/onboarding/cases/${encodeURIComponent(caseId)}`,
  proOnboardingPerson: (employeeId: string) =>
    `/pro/onboarding/people/${encodeURIComponent(employeeId)}`,
  proDeskAdmin: '/pro/desk',
  proExitHub: '/pro/exit',
  proExitCase: (caseId: string) => `/pro/exit/cases/${encodeURIComponent(caseId)}`,

  // Coming soon
  comingSoonCareerPortal: '/coming-soon/career-portal',

  // Account
  accountNotifications: '/account/notifications',
  accountAccess: '/account/access',
  accountHelp: '/account/help',

  documentFilingOutstanding: '/documents/outstanding',
  documentFilingWork: (filingId: string) => `/documents/outstanding/${encodeURIComponent(filingId)}`,
  documentFilingAssign: '/admin/document-filing',
  documentFilingReview: '/admin/document-filing/review',
  documentFilingReviewDetail: (filingId: string) =>
    `/admin/document-filing/review/${encodeURIComponent(filingId)}`,

  /** Route segment under protected layout (no leading slash). */
  workforceDirectorySegment: 'workforce',
} as const

export type AppPath = (typeof AppPaths)[keyof typeof AppPaths]
